import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { Modules } from "config";
import type { interfaces } from "inversify";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import OptionsService from "services/optionsService";
import SystemComponent, { SystemComponentValidationRules } from "entities/systemComponent";
import SystemComponentState from "entities/systemComponentState";
import EnumRepository from "repositories/enumRepository";
import EditableDetailViewModel from "../../editableDetailViewModel";
import ManualEntityValidator from "@frui.ts/validation/dist/manualEntityValidator";
import { bound } from "@frui.ts/helpers";

@Router.registerRoute({ name: Router.Self, route: ":componentId" })
export default class SystemComponentDetailViewModel extends EditableDetailViewModel<SystemComponent> {
  readonly modelName = "systemComponent";
  @observable private alreadyUsedCodes: string[] = [];
  private unsubscriptions: Array<() => void> = [];
  private manualValidator = new ManualEntityValidator<SystemComponent>(true);

  @computed
  get isEditable() {
    return false;
  }

  @computed
  get stateOptions() {
    return this.optionsService.internalEnumOptions(SystemComponentState);
  }

  constructor(
    protected itemId: string,
    protected repository: EnumRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(reaction(() => this.item.code, this.onCodeChange));
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    this.alreadyUsedCodes = [];
    return super.onDeactivate(isClosing);
  }

  protected async loadDetail() {
    const item = await this.repository.get(this.itemId, "system_components", SystemComponent);
    attachAutomaticValidator(item, {
      ...SystemComponentValidationRules,
      code: { ...SystemComponentValidationRules.code, manualValidation: this.manualValidator },
    });
    return item;
  }

  @bound
  private onCodeChange() {
    if (this.alreadyUsedCodes.includes(this.item.code)) {
      this.addAlreadyExistCodeError();
    } else {
      this.manualValidator.removeError("code");
    }
  }

  private addAlreadyExistCodeError() {
    this.manualValidator.addError("code", this.translate("codeMustBeUnique"));
  }

  async updateData(): Promise<SystemComponent> {
    try {
      return await this.repository.update(this.item, "system_components", SystemComponent);
    } catch (e: any) {
      if (e.response.status === 400) {
        this.alreadyUsedCodes.push(this.item.code);
        this.addAlreadyExistCodeError();
      } else {
        this.notificationService.addUnexpectedError();
      }
      throw e;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `systemComponents.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new SystemComponentDetailViewModel(
        itemId,
        container.get(EnumRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(OptionsService)
      );
  }
}
